/**
 * Pure 2D math for the Surface Poly tool.
 *
 * Operates in basis (u, v) coordinates — all callers project world points
 * via `basisUV` (in `surfaceBoxMath.ts`) before invoking these helpers.
 * No THREE dependency, no app-state — purely unit-testable.
 *
 *   - `clamp45InBasis`     — Shift constraint: snap drag direction to
 *                            nearest π/4 increment, magnitude preserved.
 *   - `segmentsIntersect`  — strict open-segment intersection test.
 *   - `segmentSelfIntersects` — guards against polylines that would
 *                            cross themselves (drawing-stage rejection).
 *   - `polygonSignedArea`  — area sign tells outline winding; magnitude
 *                            tells callers whether the polygon is
 *                            degenerate at confirm time.
 */

export interface UV {
  u: number;
  v: number;
}

const TAU_8 = Math.PI / 4;

/**
 * Snap the segment from `start` to `end` into the nearest 45° angle bin
 * (one of 8 around the start). Magnitude (Euclidean distance from start)
 * is preserved so a snapped corner stays at the snap target's distance.
 */
export function clamp45InBasis(start: UV, end: UV): UV {
  const du = end.u - start.u;
  const dv = end.v - start.v;
  const r = Math.hypot(du, dv);
  if (r < 1e-9) return { u: end.u, v: end.v };
  const bin = Math.round(Math.atan2(dv, du) / TAU_8);
  const theta = bin * TAU_8;
  return { u: start.u + r * Math.cos(theta), v: start.v + r * Math.sin(theta) };
}

/**
 * Strict open-segment intersection: returns true iff `(a0, a1)` and
 * `(b0, b1)` cross with parametric `t` and `s` both strictly in
 * `(eps, 1 - eps)`. Endpoint touches are excluded by the eps cushion so
 * adjacent polyline segments don't false-positive.
 *
 * Collinear overlap (parallel segments sharing more than a point) counts
 * as an intersection — those produce degenerate polygons and should also
 * be rejected by callers.
 */
export function segmentsIntersect(
  a0: UV,
  a1: UV,
  b0: UV,
  b1: UV,
  eps = 1e-9,
): boolean {
  const d1u = a1.u - a0.u;
  const d1v = a1.v - a0.v;
  const d2u = b1.u - b0.u;
  const d2v = b1.v - b0.v;
  const denom = d1u * d2v - d1v * d2u;

  if (Math.abs(denom) < eps) {
    // Parallel — check whether the two segments are collinear AND overlap
    // along their dominant axis. The cross product of (b0 - a0) with d1
    // distinguishes "different lines" (cross != 0) from "same line".
    const ru = b0.u - a0.u;
    const rv = b0.v - a0.v;
    const sameLine = Math.abs(d1u * rv - d1v * ru) < eps;
    if (!sameLine) return false;
    // Project both segments onto whichever axis (u or v) has the larger
    // d1 component to avoid divide-by-zero on axis-aligned segments.
    const useU = Math.abs(d1u) >= Math.abs(d1v);
    const ta0 = useU ? a0.u : a0.v;
    const ta1 = useU ? a1.u : a1.v;
    const tb0 = useU ? b0.u : b0.v;
    const tb1 = useU ? b1.u : b1.v;
    const aMin = Math.min(ta0, ta1);
    const aMax = Math.max(ta0, ta1);
    const bMin = Math.min(tb0, tb1);
    const bMax = Math.max(tb0, tb1);
    // Strict overlap (more than just a single touching endpoint).
    return aMin < bMax - eps && bMin < aMax - eps;
  }

  const t = ((b0.u - a0.u) * d2v - (b0.v - a0.v) * d2u) / denom;
  const s = ((b0.u - a0.u) * d1v - (b0.v - a0.v) * d1u) / denom;
  return t > eps && t < 1 - eps && s > eps && s < 1 - eps;
}

/**
 * Self-intersection check for a candidate new segment.
 *
 * @param polylineUV the existing polyline vertices in order; index 0 is
 *                   the head endpoint, last is the tail endpoint
 * @param candidate  the proposed segment plus the mode that describes
 *                   which polyline edges to skip (those that share a
 *                   vertex with the candidate, otherwise the endpoint
 *                   touch would always trigger a false positive)
 *
 * Modes:
 *   - `appendTail` — candidate connects polyline tail to a new vertex;
 *                    skip the last polyline edge.
 *   - `appendHead` — candidate connects polyline head to a new vertex;
 *                    skip the first polyline edge.
 *   - `close`      — candidate connects tail to head; skip both the
 *                    first AND the last polyline edge.
 */
export function segmentSelfIntersects(
  polylineUV: ReadonlyArray<UV>,
  candidate: {
    from: UV;
    to: UV;
    mode: "appendTail" | "appendHead" | "close";
  },
): boolean {
  const n = polylineUV.length;
  if (n < 2) return false;

  const skipFirst =
    candidate.mode === "appendHead" || candidate.mode === "close";
  const skipLast =
    candidate.mode === "appendTail" || candidate.mode === "close";

  const lastEdgeIdx = n - 2; // edge i connects polylineUV[i] → polylineUV[i+1]

  for (let i = 0; i < n - 1; i += 1) {
    if (skipFirst && i === 0) continue;
    if (skipLast && i === lastEdgeIdx) continue;
    if (
      segmentsIntersect(
        candidate.from,
        candidate.to,
        polylineUV[i],
        polylineUV[i + 1],
      )
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Signed polygon area in (u, v). Positive = CCW; negative = CW. Used by
 * `confirmSurfacePoly` to:
 *   1. detect degenerate polygons (|area| ≈ 0) and cancel cleanly
 *   2. normalize outline winding before passing to the cap triangulator
 *      (earcut / `THREE.ShapeUtils.triangulateShape` expects CCW).
 */
export function polygonSignedArea(polygonUV: ReadonlyArray<UV>): number {
  const n = polygonUV.length;
  if (n < 3) return 0;
  let acc = 0;
  for (let i = 0; i < n; i += 1) {
    const a = polygonUV[i];
    const b = polygonUV[(i + 1) % n];
    acc += a.u * b.v - b.u * a.v;
  }
  return acc / 2;
}
